import { Button } from '@dittolive/anvil'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  getAuthClient,
  getBetterAuthConfig,
  needsEmailVerification,
  usePortalAuth,
} from 'utils/portalAuth'

import { Credentials, CredentialsForm } from './CredentialsForm'
import { LoginLayout } from './LoginLayout'
import {
  clearReturnTo,
  readReturnTo,
  rememberReturnTo,
  safeReturnTo,
} from './returnTo'
import { VerificationPending } from './VerificationPending'

export function PortalLoginScene({
  mode,
  callback = false,
}: {
  mode: 'login' | 'signup'
  callback?: boolean
}) {
  const auth = usePortalAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const signup = mode === 'signup'
  const hasCallbackError = params.has('error')
  const [destination] = useState(() =>
    params.has('returnTo')
      ? safeReturnTo(params.get('returnTo'))
      : readReturnTo(),
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [pendingEmail, setPendingEmail] = useState('')
  const { providerId } = getBetterAuthConfig()
  const needsVerification = needsEmailVerification(auth)

  useEffect(() => {
    rememberReturnTo(destination)
  }, [destination])

  useEffect(() => {
    if (
      auth.isAuthenticated &&
      !auth.isLoading &&
      !pendingEmail &&
      !needsVerification &&
      !hasCallbackError
    ) {
      clearReturnTo()
      navigate(destination, { replace: true })
    }
  }, [
    auth.isAuthenticated,
    auth.isLoading,
    destination,
    navigate,
    pendingEmail,
    needsVerification,
    hasCallbackError,
  ])

  const submit = async (values: Credentials) => {
    setBusy(true)
    setError('')
    rememberReturnTo(destination)
    try {
      if (signup) {
        const result = await getAuthClient().signUp.email({
          name: values.name ?? '',
          email: values.email,
          password: values.password,
          callbackURL: '/verify-email',
        })
        if (result.error) {
          setError(
            result.error.message ||
              'Could not create your account. Please try again.',
          )
          return
        }
        setPendingEmail(values.email)
        return
      }

      const result = await getAuthClient().signIn.email({
        email: values.email,
        password: values.password,
      })
      if (result.error) {
        if (result.error.code === 'EMAIL_NOT_VERIFIED') {
          setPendingEmail(values.email)
        } else {
          setError(result.error.message || 'Invalid email or password.')
        }
        return
      }
      auth.selectProvider('better-auth')
      if (await auth.refreshSession()) {
        clearReturnTo()
        navigate(destination, { replace: true })
      } else {
        setError('We could not start your Portal session. Please log in again.')
      }
    } catch {
      setError('Could not reach the authentication service. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const bridge = async () => {
    if (!providerId) return
    setBusy(true)
    setError('')
    rememberReturnTo(destination)
    try {
      const result = await getAuthClient().signIn.sso({
        providerId,
        callbackURL: `${window.location.origin}/login/callback`,
        errorCallbackURL: `${window.location.origin}/login?error=sso`,
      })
      if (result.error || !result.data?.url) {
        setError(
          'Existing-customer login is unavailable. Please try again or use the Auth0 login link below.',
        )
        setBusy(false)
        return
      }
      auth.selectProvider('better-auth')
      window.location.assign(result.data.url)
    } catch {
      setError('Could not start existing-customer login. Please try again.')
      setBusy(false)
    }
  }

  const callbackError = hasCallbackError
    ? 'Login was canceled or could not be completed. Please try again.'
    : callback && !auth.isLoading && !auth.isAuthenticated
      ? 'No active session was found after login. Please try again.'
      : ''
  const legacyUrl = `/login/auth0?returnTo=${encodeURIComponent(destination)}`

  return (
    <LoginLayout
      title={
        pendingEmail || needsVerification
          ? 'Verify your email'
          : signup
            ? 'Create your Portal account'
            : 'Log in to Portal'
      }
    >
      {pendingEmail || needsVerification ? (
        <VerificationPending
          email={pendingEmail || auth.user?.email || ''}
          onBack={async () => {
            if (needsVerification) await auth.logout()
            setPendingEmail('')
            navigate('/login', { replace: true })
          }}
        />
      ) : (
        <>
          {(error || callbackError || auth.error) && (
            <p role="alert">
              {error ||
                callbackError ||
                'Could not restore your session. Please log in again.'}
            </p>
          )}
          {auth.isLoading ? (
            <p role="status">Checking your session…</p>
          ) : (
            <>
              <CredentialsForm
                key={mode}
                signup={signup}
                busy={busy}
                onSubmit={submit}
              />
              {providerId && (
                <Button
                  variant="secondary"
                  type="button"
                  className="w-full"
                  disabled={busy}
                  onClick={bridge}
                >
                  {busy
                    ? 'Please wait…'
                    : 'Existing customer? Continue with Auth0'}
                </Button>
              )}
              <p>
                <Link
                  className="underline"
                  to={`${signup ? '/login' : '/signup'}?returnTo=${encodeURIComponent(destination)}`}
                >
                  {signup
                    ? 'Already have an account? Log in'
                    : 'New to Ditto? Create an account'}
                </Link>
              </p>
            </>
          )}
        </>
      )}
      <p>
        <Link className="underline" to={legacyUrl}>
          Log in directly with Auth0, including Google
        </Link>
      </p>
    </LoginLayout>
  )
}
